import { logToPct, pctToLog, type Driver, type ModelPayload } from '@quoting/ladder'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { cx, DataTable, ErrorBox, Field, InfoBox, KpiCard, Panel, ScreenHeading, Signed, Spinner, Tag, WarnBox } from '../../components/ui'
import { api, ApiError, errorMessage } from '../../lib/api'
import { amount, dateTime, logPct, money0, pct, signedAmount, signedPct } from '../../lib/format'
import type { Catalogue, ImpactReport, VersionDetail, VersionSummary } from '../../lib/types'
import { VERSION_TONE } from './Versions'
import { localName, useT } from '../../lib/i18n'

type EffectEdits = Record<string, string> // `${driverId}|${level}` → text as typed (pct for relative, € for absolute)

const fmtNum = (n: number, digits = 3) => {
  const s = n.toFixed(digits)
  return s.replace(/\.?0+$/, '')
}

function effectValue(p: ModelPayload, driverId: string, level: string): number | null {
  return p.drivers.find((d) => d.id === driverId)?.effects.find((e) => e.level === level)?.value ?? null
}

/** §8.6 — the only way a weight changes inside the tool. */
export function VersionEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t, lang } = useT()
  const tr = t
  const [d, setD] = useState<VersionDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [edits, setEdits] = useState<EffectEdits>({})
  const [intercept, setIntercept] = useState<string | null>(null)
  const [absBase, setAbsBase] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [negative, setNegative] = useState<{ driverId: string; level: string; value: number }[] | null>(null)
  const [confirmText, setConfirmText] = useState('')
  const [impact, setImpact] = useState<ImpactReport | null>(null)
  const [impactBusy, setImpactBusy] = useState(false)
  const [publishNote, setPublishNote] = useState('')
  const [tab, setTab] = useState<'weights' | 'payload'>('weights')

  const load = useCallback(async () => {
    if (!id) return
    try {
      const v = await api.get<VersionDetail>(`/api/model-versions/${id}`)
      setD(v)
      setEdits({})
      setIntercept(null)
      setAbsBase(null)
      setNote(v.payload.note)
    } catch (e) {
      setError(errorMessage(e))
    }
  }, [id])
  useEffect(() => {
    load()
  }, [load])

  const isDraft = d?.summary.status === 'draft'
  const p = d?.payload
  const parent = d?.parentPayload ?? null

  const dirty = Object.keys(edits).length > 0 || intercept !== null || absBase !== null || (p && note !== p.note)

  const buildPatch = (confirmNegative = false) => {
    const effects: { driverId: string; level: string; value: number }[] = []
    for (const [k, text] of Object.entries(edits)) {
      const [driverId, level] = k.split('|') as [string, string]
      const drv = p!.drivers.find((x) => x.id === driverId)!
      const n = Number(text.replace(',', '.'))
      if (!Number.isFinite(n)) throw new Error(`"${text}" is not a number (${drv.name} / ${level})`)
      effects.push({ driverId, level, value: drv.kind === 'relative' ? pctToLog(n) : n })
    }
    const patch: Record<string, unknown> = { note, confirmNegative }
    if (effects.length) patch.effects = effects
    if (intercept !== null) patch.intercept = Number(intercept.replace(',', '.'))
    if (absBase !== null) patch.absBase = Number(absBase.replace(',', '.'))
    return patch
  }

  const save = async (confirmNegative = false) => {
    if (!id) return
    setSaving(true)
    setError(null)
    try {
      const v = await api.patch<VersionDetail>(`/api/model-versions/${id}`, buildPatch(confirmNegative))
      setD(v)
      setEdits({})
      setIntercept(null)
      setAbsBase(null)
      setNegative(null)
      setConfirmText('')
      setImpact(null)
    } catch (e) {
      if (e instanceof ApiError && e.code === 'negative_coefficient') {
        setNegative((e.details as { negative: { driverId: string; level: string; value: number }[] }).negative)
      } else setError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  const loadImpact = async () => {
    if (!id) return
    setImpactBusy(true)
    setError(null)
    try {
      setImpact(await api.get<ImpactReport>(`/api/model-versions/${id}/impact`))
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setImpactBusy(false)
    }
  }

  const publish = async () => {
    if (!id) return
    setSaving(true)
    setError(null)
    try {
      await api.post(`/api/model-versions/${id}/publish`, { note: publishNote })
      navigate('/admin/versions')
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  const [catalogue, setCatalogue] = useState<Catalogue | null>(null)
  useEffect(() => {
    api.get<Catalogue | null>('/api/catalogue').then(setCatalogue).catch(() => setCatalogue(null))
  }, [])
  const catDriver = (id: string) => catalogue?.drivers.find((x) => x.id === id)
  const blocks = useMemo(() => p?.blocks ?? [], [p])
  const driversByBlock = useMemo(() => {
    const m = new Map<string, Driver[]>()
    for (const drv of p?.drivers ?? []) m.set(drv.block, [...(m.get(drv.block) ?? []), drv])
    return m
  }, [p])

  // a published version never changes; editing continues in a draft from it (an open one if there is)
  const editAsDraft = async () => {
    if (!d) return
    setError(null)
    try {
      const all = await api.get<VersionSummary[]>('/api/model-versions')
      const open = all.find((v) => v.status === 'draft' && v.parentVersionId === d.summary.id)
      const target = open ? open.id : (await api.post<VersionDetail>('/api/model-versions/draft', { from: d.summary.id })).summary.id
      navigate(`/admin/versions/${target}`)
    } catch (e) {
      setError(errorMessage(e))
    }
  }

  const deleteDraft = async () => {
    if (!d || !window.confirm(t('Delete this draft? Its edits are lost; the published versions stay as they are.'))) return
    setError(null)
    try {
      await api.del(`/api/model-versions/${d.summary.id}`)
      navigate(d.summary.parentVersionId ? `/admin/versions/${d.summary.parentVersionId}` : '/admin/versions')
    } catch (e) {
      setError(errorMessage(e))
    }
  }

  if (error && !d) return <ErrorBox>{error}</ErrorBox>
  if (!d || !p) return <Spinner />
  const s = d.summary
  const short = (x: string) => x.slice(0, 8)

  return (
    <div className="space-y-4">
      <ScreenHeading
        title={
          <span className="flex items-center gap-2">
            {t('Version')} {short(s.id)} <Tag tone={VERSION_TONE[s.status] ?? 'neutral'}>{t(s.status)}</Tag>
            {s.source === 'hand_edited' ? <Tag tone="warn">{t('hand-edited')}</Tag> : <Tag tone="info">{t('imported')}</Tag>}
          </span>
        }
        subtitle={
          <>
            {s.parentVersionId ? `${t('Parent')} ${short(s.parentVersionId)} · ` : ''}
            {s.publishedAt ? `${t('published')} ${dateTime(s.publishedAt)} ${t('by')} ${s.publishedByName ?? ''}` : `${t('created')} ${dateTime(s.createdAt)} ${t('by')} ${s.createdByName ?? ''}`} · {t('basis')}{' '}
            {p.basis === 'delivered' ? t('delivered') : t('ex works')} · {p.currency} · {t('ratings from')} {short(d.ratings.ownerVersionId)} ({d.ratings.total})
            {!isDraft && ` · ${t('immutable')}`}
          </>
        }
        right={
          <div className="flex gap-2">
            <button type="button" className="btn" onClick={() => navigate('/admin/versions')}>
              {t('All versions')}
            </button>
            {!isDraft && (
              <button type="button" className="btn btn-primary" onClick={editAsDraft}>
                {t('Edit')}
              </button>
            )}
          </div>
        }
      />
      {error && <ErrorBox>{error}</ErrorBox>}

      {isDraft && (
        <Panel>
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('Draft note')}>
              <input className="field w-[30rem] max-w-full min-w-[20rem]" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('What this revision is for')} />
            </Field>
            <button type="button" className="btn btn-primary" disabled={!dirty || saving} onClick={() => save(false)}>
              {saving ? t('Saving…') : t('Save draft')}
            </button>
            {dirty && (
              <button type="button" className="btn" disabled={saving} onClick={load}>
                {t('Discard changes')}
              </button>
            )}
            <button type="button" className="btn btn-danger" disabled={saving} onClick={deleteDraft}>
              {t('Delete draft')}
            </button>
            <span className="text-xs text-slate-500">{t('Edits do not move live prices until the draft is published.')}</span>
          </div>
          {negative && (
            <div className="mt-3 space-y-2">
              <WarnBox>
                <b>A negative relative coefficient makes a rung that subtracts.</b> It cannot be explained to a customer as a surcharge. It is stored if you
                confirm — articles that sell below their own cost are a real case — but type <b>SUBTRACT</b> to do so.
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {negative.map((n) => (
                    <li key={`${n.driverId}|${n.level}`}>
                      {p.drivers.find((x) => x.id === n.driverId)?.name ?? n.driverId} · level {n.level} → {logPct(n.value)}
                    </li>
                  ))}
                </ul>
              </WarnBox>
              <div className="flex items-center gap-2">
                <input className="field w-40" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="SUBTRACT" />
                <button type="button" className="btn btn-danger" disabled={confirmText !== 'SUBTRACT' || saving} onClick={() => save(true)}>
                  {t('Store anyway')}
                </button>
                <button type="button" className="btn" onClick={() => setNegative(null)}>
                  {t('Cancel')}
                </button>
              </div>
            </div>
          )}
        </Panel>
      )}

      <div className="flex gap-1">
        {(['weights', 'payload'] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} className={cx('rounded-md px-2.5 py-1 text-sm transition', tab === t ? 'bg-slate-800/60 text-slate-100' : 'text-slate-400 hover:text-slate-200')}>
            {t === 'weights' ? tr('Weights') : tr('Payload')}
          </button>
        ))}
      </div>

      {tab === 'weights' && (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <Panel title={t('Intercept')} subtitle={t('Log-space constant applied to every price before any driver. Enter as a percentage.')}>
              <div className="flex items-center gap-3">
                <input className="field w-32 tabular" disabled={!isDraft} value={intercept ?? fmtNum(logToPct(p.intercept), 3)} onChange={(e) => setIntercept(e.target.value)} />
                <span className="text-xs text-slate-500">%</span>
                {parent && parent.intercept !== p.intercept && <span className="text-xs text-slate-500">{t('parent')} {logPct(parent.intercept)}</span>}
              </div>
            </Panel>
            <Panel title={t('Base surcharge')} subtitle={t('Euros per unit added with the absolute drivers, once per price.')}>
              <div className="flex items-center gap-3">
                <input className="field w-32 tabular" disabled={!isDraft} value={absBase ?? fmtNum(p.absBase, 2)} onChange={(e) => setAbsBase(e.target.value)} />
                <span className="text-xs text-slate-500">{t('€/unit')}</span>
                {parent && parent.absBase !== p.absBase && <span className="text-xs text-slate-500">{t('parent')} {amount(parent.absBase)}</span>}
              </div>
            </Panel>
          </div>
          {blocks.map((b) => (
            <Panel key={b.key} title={localName(lang, b.name, b.nameDe)} subtitle={t('{n} drivers', { n: (driversByBlock.get(b.key) ?? []).length })}>
              <div className="grid gap-3 lg:grid-cols-2">
                {(driversByBlock.get(b.key) ?? []).map((drv) => {
                  const parentDrv = parent?.drivers.find((x) => x.id === drv.id)
                  const rel = drv.kind === 'relative'
                  const ratings = d.ratings.byDriver.find((r) => r.driverId === drv.id)?.count ?? 0
                  return (
                    <div key={drv.id} className="rounded-md border border-slate-800 bg-slate-900/40 p-3">
                      <div className="mb-2 flex items-baseline justify-between gap-2">
                        <div>
                          <span className="text-sm font-semibold text-slate-200">{localName(lang, drv.name, drv.nameDe)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px]">
                          <Tag tone={rel ? 'info' : 'warn'}>{rel ? t('relative') : t('absolute · €/unit')}</Tag>
                          <Tag>{t(drv.grain)}</Tag>
                          <span className="text-slate-500" title={t('ratings resolving to this driver')}>{ratings} {t('ratings')}</span>
                        </div>
                      </div>
                      {catDriver(drv.id)?.anchorLow && (
                        <div className="mb-2 grid grid-cols-2 gap-2 text-[11px] text-slate-500">
                          <div><span className="font-semibold text-slate-400">{catDriver(drv.id)!.levels[0]}</span> · {catDriver(drv.id)!.anchorLow}</div>
                          <div className="text-right"><span className="font-semibold text-slate-400">{catDriver(drv.id)!.levels[catDriver(drv.id)!.levels.length - 1]}</span> · {catDriver(drv.id)!.anchorHigh}{catDriver(drv.id)!.reversed ? ' ⇄' : ''}</div>
                        </div>
                      )}
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                            <th className="px-2 py-1 text-left font-medium">{t('Level')}</th>
                            <th className="px-2 py-1 text-right font-medium">{rel ? t('Effect %') : t('Effect €/unit')}</th>
                            <th className="px-2 py-1 text-right font-medium">{t('Stored')}</th>
                            {parent && <th className="px-2 py-1 text-right font-medium">{t('Parent')}</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {drv.effects.map((e) => {
                            const k = `${drv.id}|${e.level}`
                            const pv = parentDrv?.effects.find((x) => x.level === e.level)?.value ?? null
                            const moved = pv !== null && Math.abs(pv - e.value) > 1e-12
                            const edited = k in edits
                            const display = rel ? fmtNum(logToPct(e.value), 3) : fmtNum(e.value, 2)
                            return (
                              <tr key={e.level} className={cx('border-t border-slate-800/70', moved && 'bg-amber-500/5')}>
                                <td className="px-2 py-1 tabular text-slate-300">{(lang === 'de' ? catDriver(drv.id)?.levelLabelsDe?.[catDriver(drv.id)!.levels.indexOf(e.level)] : undefined) ?? catDriver(drv.id)?.levelLabels?.[catDriver(drv.id)!.levels.indexOf(e.level)] ?? e.level}</td>
                                <td className="px-2 py-1 text-right">
                                  <input
                                    className={cx('field w-28 text-right tabular', edited && 'border-sky-500', e.value < 0 && 'text-rose-300')}
                                    disabled={!isDraft}
                                    value={edited ? edits[k] : display}
                                    onChange={(ev) => setEdits((prev) => ({ ...prev, [k]: ev.target.value }))}
                                  />
                                </td>
                                <td className="px-2 py-1 text-right text-xs tabular text-slate-500" title="the coefficient as stored">{rel ? e.value.toFixed(6) : amount(e.value)}</td>
                                {parent && (
                                  <td className="px-2 py-1 text-right text-xs tabular">
                                    {pv === null ? (
                                      <span className="text-slate-600">–</span>
                                    ) : moved ? (
                                      <span className="text-amber-300">{rel ? logPct(pv) : amount(pv)} → <Signed value={e.value - pv}>{rel ? `${signedAmount(logToPct(e.value) - logToPct(pv)).replace(',00', '')} pp` : signedAmount(e.value - pv)}</Signed></span>
                                    ) : (
                                      <span className="text-slate-500">{rel ? logPct(pv) : amount(pv)}</span>
                                    )}
                                  </td>
                                )}
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
                })}
              </div>
            </Panel>
          ))}
        </div>
      )}

      {tab === 'payload' && (
        <Panel title={t('Payload')} subtitle={t('The version as stored.')}>
          <pre className="max-h-[36rem] overflow-auto rounded-md bg-slate-900/40 p-3 font-mono text-xs text-slate-300">{JSON.stringify(p, null, 2).replace(/\u0000/g, '␀')}</pre>
        </Panel>
      )}

      {isDraft && (
        <Panel
          title={t('Impact, before publishing')}
          subtitle={t('For each changed level: how many article–customer pairs move, their share of revenue, the change in target, and the largest movements.')}
          right={
            <button type="button" className="btn" onClick={loadImpact} disabled={impactBusy || dirty}>
              {impactBusy ? t('Computing…') : impact ? t('Recompute') : t('Compute impact')}
            </button>
          }
        >
          {dirty && <InfoBox>{t('Save the draft first; the preview prices the saved draft.')}</InfoBox>}
          {impact && !dirty && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <KpiCard label={t('Pairs priced')} value={impact.totalPairs - impact.unratedPairs} foot={`${impact.unratedPairs} ${t('unrated skipped')} · ${impact.window.from} – ${impact.window.to}`} />
                <KpiCard label={t('Pairs moving')} value={impact.whole.pairs} foot={`${pct(impact.whole.revenueShare, 0)} ${t('of revenue')}`} />
                <KpiCard label={t('Aggregate Δ target')} value={<Signed value={impact.whole.deltaEurAnnual}>{money0(impact.whole.deltaEurAnnual, p.currency)}</Signed>} foot={t('at last year’s volumes')} />
                <KpiCard label={t('Aggregate Δ %')} value={<Signed value={impact.whole.deltaPct}>{signedPct(impact.whole.deltaPct, 2)}</Signed>} foot={t('volume-weighted')} />
              </div>
              {impact.changes.length === 0 && <p className="text-xs text-slate-500">{t('This draft is identical to its parent.')}</p>}
              {impact.changes.map((c, i) => (
                <div key={i} className="rounded-md border border-slate-800 bg-slate-900/40 p-3">
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-3">
                    <div className="text-sm text-slate-200">
                      {c.kind === 'effect' ? (
                        <>
                          <b>{c.driverName}</b> · level {c.level}:{' '}
                          <span className="text-slate-400">
                            {c.from === null ? 'absent' : String(typeof c.from === 'number' && p.drivers.find((x) => x.id === c.driverId)?.kind === 'relative' ? logPct(c.from as number) : c.from)} →{' '}
                            {c.to === null ? 'removed' : String(typeof c.to === 'number' && p.drivers.find((x) => x.id === c.driverId)?.kind === 'relative' ? logPct(c.to as number) : c.to)}
                          </span>
                        </>
                      ) : (
                        <>
                          <b className="font-mono text-xs">{c.path?.replace(/\u0000/g, ' › ')}</b> <span className="text-slate-400">{JSON.stringify(c.from ?? null)} → {JSON.stringify(c.to ?? null)}</span>{' '}
                          <span className="text-xs text-slate-500">(whole draft: every pair whose target moved)</span>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-slate-400">
                      <b className="tabular text-slate-200">{c.pairs}</b> {t('pairs')} · <b className="tabular text-slate-200">{pct(c.revenueShare, 1)}</b> {t('of revenue')} ·{' '}
                      <Signed value={c.deltaEurAnnual}>{money0(c.deltaEurAnnual, p.currency)}</Signed> · <Signed value={c.deltaPct}>{signedPct(c.deltaPct, 2)}</Signed>
                    </div>
                  </div>
                  <DataTable
                    rows={c.top}
                    rowKey={(r) => `${r.customerId}|${r.productId}`}
                    columns={[
                      { key: 'c', header: t('Customer'), render: (r) => r.customerName },
                      { key: 'p', header: t('Article'), render: (r) => r.productName },
                      { key: 'rev', header: t('Revenue 12m'), align: 'right', render: (r) => money0(r.revenue, p.currency) },
                      { key: 'b', header: t('Before'), align: 'right', render: (r) => amount(r.before) },
                      { key: 'a', header: t('After'), align: 'right', render: (r) => <span className="text-slate-100">{amount(r.after)}</span> },
                      { key: 'd', header: 'Δ', align: 'right', render: (r) => <Signed value={r.delta}>{signedAmount(r.delta)} <span className="text-xs opacity-80">({signedPct(r.deltaPct, 2)})</span></Signed> },
                      { key: 'k', header: '', render: (r) => (r.clamped ? <Tag tone="warn">{t(r.clamped)} {t('by corridor')}</Tag> : null) },
                    ]}
                  />
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {isDraft && (
        <Panel title={t('Publish')} subtitle={t('Makes this draft the active version. The current active version is retired. A note is required and audited.')}>
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('Publish note')}>
              <input className="field w-[30rem] max-w-full" value={publishNote} onChange={(e) => setPublishNote(e.target.value)} placeholder={t('Signed off by the pricing committee on …')} />
            </Field>
            <button type="button" className="btn btn-pos" disabled={!publishNote.trim() || dirty || saving} onClick={publish}>
              {t('Publish')}
            </button>
            {dirty && <span className="text-xs text-amber-300">{t('Save or discard your edits first.')}</span>}
            {!impact && !dirty && <span className="text-xs text-slate-500">{t('Compute the impact above before publishing.')}</span>}
          </div>
        </Panel>
      )}
    </div>
  )
}
